import React, { useState } from "react";
import { useTheme } from "@react-navigation/native";
import { Theme } from "@theme";
import { Modal, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Calendar } from "react-native-calendars";
import dayjs, { Dayjs } from "dayjs";
import DatePicker from "react-native-date-picker";
import { useScreen } from "@hooks";
import CalendarComponent from "../../calendar/components/CalendarComponent";
import { BottomModalBase } from "@components";
import { useLanguage } from "@context";

const ScheduleCalendarModal = ({isVisible, setIsVisible, date, time, onDateSelect, onTimeChange}: {isVisible: boolean, setIsVisible: (isVisible: boolean) => void, date: string, time: string, onDateSelect: (date: string) => void, onTimeChange: (time: string) => void}) => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const {isWeb, isMobile} = useScreen();
    const { t } = useLanguage()
    const [timePickerVisible, setTimePickerVisible] = useState(false)

    const renderContent = () => {
      return (
        <>
          {/* Calendrier */}
          <View style={styles.calendarContainer}>
            {isWeb && <CalendarComponent events={[]} onDayChange={(day: Dayjs | null) => {
                onDateSelect(day?.format('DD-MM-YYYY') || '');
              }}/>}
            {!isWeb && <Calendar
              markedDates={{
                [dayjs(date, 'DD-MM-YYYY').format('YYYY-MM-DD')]: {
                  selected: true,
                  selectedColor: theme.colors.primary,
                },
              }}
              minDate={dayjs().format('YYYY-MM-DD')}
              onDayPress={(day: any) => {
                onDateSelect(dayjs(day.dateString, 'YYYY-MM-DD').format('DD-MM-YYYY'));
              }}
            />}
          </View>
          
          {/* Sélecteur d'heure */}
          <View style={styles.timeSelector}>
            <Text style={styles.timeLabel}>{t('calendarModal.time')}</Text>
            <View>
              {!isWeb && <TouchableOpacity onPress={() => setTimePickerVisible(true)}>
                <Text style={styles.timeText}>{time}</Text>
              </TouchableOpacity>}
              <DatePicker
                modal={!isWeb}
                open={timePickerVisible}
                date={dayjs(time, 'HH:mm').toDate()}
                onDateChange={(date) => {
                  onTimeChange(dayjs(date).format('HH:mm'))
                  setTimePickerVisible(false)
                }}
                onConfirm={(date) => {
                  onTimeChange(dayjs(date).format('HH:mm'))
                  setTimePickerVisible(false)
                }}
                onCancel={() => setTimePickerVisible(false)}
                mode="time"
                locale="fr"
                is24hourSource="locale"
              />
            </View>
          </View>
        </>
      )
    }
    return isWeb && !isMobile? <BottomModalBase isVisible={isVisible} setIsVisible={() => setIsVisible(false)} children={renderContent()} fitParent hasCloseButton/> : (
      <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={() => setIsVisible(false)}
    >
      <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.overlayTouchable}
            activeOpacity={1}
            onPress={() => setIsVisible(false)}
          />
          <View style={styles.modalContainer}>
            <View style={styles.handle} />
            {renderContent()}
            {/* Bouton Programmer */}
            <View style={styles.scheduleButtonContainer}>
              <TouchableOpacity 
                style={styles.scheduleButton}
                onPress={() => {
                  setIsVisible(false);
                }}
              >
                <Text style={styles.scheduleButtonText}>{t('calendarModal.schedule')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
    </Modal>
    )
} 

const getStyles = (theme: Theme) => StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#F2F2F7',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: 'white',
      borderBottomWidth: 0.5,
      borderBottomColor: '#C7C7CC',
    },
    backButton: {
      padding: 8,
    },
    contactInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginHorizontal: 16,
    },
    contactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      marginRight: 12,
    },
    contactDetails: {
      flex: 1,
    },
    contactName: {
      fontSize: 16,
      fontWeight: '600',
      color: '#000',
    },
    contactStatus: {
      fontSize: 13,
      color: '#8E8E93',
      marginTop: 2,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerAction: {
      padding: 6,
      marginLeft: 2,
    },
    content: {
      flex: 1,
    },
    conversationArea: {
      flex: 1,
      paddingHorizontal: 16,
      paddingTop: 20,
    },
    dateSection: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: '#F2F2F7',
    },
    dateLine: {
      flex: 1,
      height: 1,
      backgroundColor: '#E5E5EA',
    },
    dateText: {
      paddingHorizontal: 12,
      fontSize: 12,
      color: '#007AFF',
      fontWeight: '500',
    },
    messageContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginBottom: 8,
    },
    messageBubble: {
      backgroundColor: '#007AFF',
      borderRadius: 18,
      paddingHorizontal: 16,
      paddingVertical: 10,
      maxWidth: '80%',
    },
    messageBubbleText: {
      fontSize: 16,
      color: 'white',
    },
    deleteMessageButton: {
      marginLeft: 8,
      padding: 4,
    },
    scheduleInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginBottom: 4,
    },
    scheduleInfoText: {
      fontSize: 12,
      color: '#8E8E93',
      fontStyle: 'italic',
    },
    editScheduleButton: {
      marginLeft: 8,
      padding: 4,
    },
    footer: {
      backgroundColor: 'white',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 0.5,
      borderTopColor: '#C7C7CC',
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    messageInputContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#F2F2F7',
      borderRadius: 30,
      paddingLeft: 16,
      paddingRight: 4,
      paddingVertical: 4,
      minHeight: 44,
    },
    messageInput: {
      flex: 1,
      fontSize: 16,
      color: '#000',
      maxHeight: 100,
      paddingVertical: 10,
      paddingRight: 8,
    },
    sendButton: {
      backgroundColor: '#007AFF',
      borderRadius: 20,
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 4,
    },
    calendarButton: {
      backgroundColor: '#00C851',
      borderRadius: 20,
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      justifyContent: 'flex-end',
    },
    overlayTouchable: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    modalContainer: {
      backgroundColor: 'white',
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      minHeight: 200,
    },
    handle: {
      width: 36,
      height: 4,
      backgroundColor: '#C7C7CC',
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 8,
      marginBottom: 8,
    },
    // Styles pour le calendrier
    calendarContainer: {
      padding: 20,
    },
    timeSelector: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 20,
      marginBottom: 30,
    },
    timeLabel: {
      fontSize: 16,
      color: '#000',
      fontWeight: '500',
      marginRight: 20,
    },
    timeText: {
      fontSize: 16,
      color: '#000',
      fontWeight: '500',
    },
    scheduleButtonContainer: {
      paddingHorizontal: 20,
      marginBottom: 20,
    },
    scheduleButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 25,
      paddingVertical: 16,
      alignItems: 'center',
    },
    scheduleButtonText: {
      fontSize: 17,
      fontWeight: '600',
      color: 'white',
    },
    safeArea: {
      flex: 1,
    },
  });

  export default ScheduleCalendarModal;